import { supabase } from '../../lib/supabaseClient';
import type { Tables } from '../../types/database.types';
import type { HearingPayload, HearingPerson } from './hearingForm';

export type DossierHearing = Tables<'dossier_hearings'>;
export type DossierHearingAttendee = Tables<'dossier_hearing_attendees'>;

export type HearingWrite = HearingPayload & {
  people: HearingPerson[];
};

function attendeeRows(hearingId: string, people: HearingPerson[]) {
  return people.map((person) => ({
    hearing_id: hearingId,
    collaborator_id: person.kind === 'collaborator' ? person.id : null,
    contact_id: person.kind === 'contact' ? person.id : null,
  }));
}

async function replaceHearingAttendees(hearingId: string, people: HearingPerson[]): Promise<void> {
  const { error: deleted } = await supabase
    .from('dossier_hearing_attendees')
    .delete()
    .eq('hearing_id', hearingId);
  if (deleted) throw new Error(`Failed to replace hearing attendees: ${deleted.message}`);
  if (people.length === 0) return;
  const { error } = await supabase
    .from('dossier_hearing_attendees')
    .insert(attendeeRows(hearingId, people));
  if (error) throw new Error(`Failed to save hearing attendees: ${error.message}`);
}

export async function listDossierHearings(dossierId: string): Promise<DossierHearing[]> {
  const { data, error } = await supabase
    .from('dossier_hearings')
    .select('*')
    .eq('dossier_id', dossierId)
    .order('starts_at', { ascending: true });
  if (error) throw new Error(`Failed to list hearings: ${error.message}`);
  return data ?? [];
}

export async function listHearingAttendees(hearingId: string): Promise<DossierHearingAttendee[]> {
  const { data, error } = await supabase
    .from('dossier_hearing_attendees')
    .select('*')
    .eq('hearing_id', hearingId);
  if (error) throw new Error(`Failed to list hearing attendees: ${error.message}`);
  return data ?? [];
}

export async function listAttendeesForHearings(hearingIds: string[]): Promise<DossierHearingAttendee[]> {
  if (hearingIds.length === 0) return [];
  const { data, error } = await supabase
    .from('dossier_hearing_attendees')
    .select('*')
    .in('hearing_id', hearingIds);
  if (error) throw new Error(`Failed to list hearing attendees: ${error.message}`);
  return data ?? [];
}

export async function createDossierHearing(input: HearingWrite & {
  dossier_id: string;
  created_by?: string | null;
}): Promise<DossierHearing> {
  const { data, error } = await supabase
    .from('dossier_hearings')
    .insert({
      dossier_id: input.dossier_id,
      label: input.label,
      description: input.description,
      court: input.court,
      starts_at: input.starts_at,
      ends_at: input.ends_at,
      notes: input.notes,
      owner_id: input.owner_id,
      event_type_id: input.event_type_id,
      created_by: input.created_by ?? null,
    })
    .select('*')
    .single();
  if (error) throw new Error(`Failed to create hearing: ${error.message}`);
  await replaceHearingAttendees(data.id, input.people);
  return data;
}

export async function updateDossierHearing(id: string, patch: HearingWrite): Promise<DossierHearing> {
  const { data, error } = await supabase
    .from('dossier_hearings')
    .update({
      label: patch.label,
      description: patch.description,
      court: patch.court,
      starts_at: patch.starts_at,
      ends_at: patch.ends_at,
      notes: patch.notes,
      owner_id: patch.owner_id,
      event_type_id: patch.event_type_id,
    })
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw new Error(`Failed to update hearing: ${error.message}`);
  await replaceHearingAttendees(id, patch.people);
  return data;
}

export async function deleteDossierHearing(id: string): Promise<void> {
  const { error } = await supabase.from('dossier_hearings').delete().eq('id', id);
  if (error) throw new Error(`Failed to delete hearing: ${error.message}`);
}
